import type { PersonalisationSlot } from '../context/personalisation-typedefs';
import { logError, logWarning } from './logging.js';

// ─── Public types ─────────────────────────────────────────────────────────────

/**
 * One customer-facing row: a stone (COMPONENT), plus its optional year and moment
 * (ALPHANUMERIC slots).
 */
export interface PairedRow {
  /** Zero-based row position; row `index` renders as "stone {index + 1}". */
  index: number;
  componentSlot: PersonalisationSlot;
  /** The row's moment: any ALPHANUMERIC slot that is not numbers-only. */
  textSlot: PersonalisationSlot | null;
  /** The row's year: a numbers-only ALPHANUMERIC slot (`allowNumbers && !allowLetters`). */
  yearSlot: PersonalisationSlot | null;
}

export interface PairedSlotModel {
  rows: PairedRow[];
  /** Slots the model could not pair: text slots with no row and unsupported slot types. */
  unmatchedSlots: PersonalisationSlot[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** The component slot type that opens a row. */
const COMPONENT_SLOT_TYPE = 'COMPONENT';

/** The text slot type paired into a row. */
const TEXT_SLOT_TYPE = 'ALPHANUMERIC';

/** The restriction flags the role is read from; Liquid sends them as booleans or 'true'/'false'. */
type RestrictionFlags = { allowNumbers?: boolean | string; allowLetters?: boolean | string };

/**
 * The config problems already reported on this page, each as the problem plus its text slot ids.
 * The model is rebuilt on every selection change, so without this the same problem would be sent
 * to Sentry again on each one.
 */
const reportedProblems = new Set<string>();

// ─── Private helpers ──────────────────────────────────────────────────────────

/** The numeric `orderIndex` the sort and the row lookup share: anything non-numeric is 0. */
function getOrderIndex(slot: PersonalisationSlot): number {
  return Number(slot.orderIndex) || 0;
}

/**
 * The row a text slot belongs to: its `orderIndex` when that is a whole number above 0, else null.
 * Rows are numbered from 1, so a text slot with no number (or 0) never joins a row, even when a
 * stone sits at 0. The admin has the same rule in `apps/gadget/web/utils/pairedRows.ts`
 * (`getRowNumber`); change them together.
 */
function getRowNumber(slot: PersonalisationSlot): number | null {
  const rowNumber = Number(slot.orderIndex);
  return Number.isInteger(rowNumber) && rowNumber > 0 ? rowNumber : null;
}

/**
 * Sorts slots by their numeric `orderIndex`, treating anything non-numeric as 0.
 * Array.prototype.sort is stable, so equal order indexes keep their input order.
 */
function sortSlotsByOrderIndex(slots: PersonalisationSlot[]): PersonalisationSlot[] {
  return [...slots].sort((slotA, slotB) => getOrderIndex(slotA) - getOrderIndex(slotB));
}

/** Same boolean-or-'true' reading as the alphanumeric slot's own view model. */
function isFlagOn(value: boolean | string | undefined): boolean {
  return value === true || value === 'true';
}

/**
 * True for a numbers-only text slot — the row's year. The role comes from the restrictions
 * the Gadget admin already authors, so no new schema is needed to tell a year from a moment.
 */
function isYearSlot(slot: PersonalisationSlot): boolean {
  const { allowNumbers, allowLetters } = slot as PersonalisationSlot & RestrictionFlags;
  return isFlagOn(allowNumbers) && !isFlagOn(allowLetters);
}

/**
 * Runs `report` the first time this problem is seen for this set of text slots on the page, and
 * never again for the same set, whatever order the slots come in.
 */
function reportOnce(problem: string, textSlots: PersonalisationSlot[], report: () => void): void {
  const key = `${problem}:${textSlots
    .map((slot) => String(slot.id))
    .sort()
    .join(',')}`;
  if (reportedProblems.has(key)) return;
  reportedProblems.add(key);
  report();
}

/**
 * The legacy pairing: text slots zipped onto stones by position, every one of them a moment.
 * Only called when there are as many text slots as stones, so every row gets exactly one.
 */
function zipByPosition(componentSlots: PersonalisationSlot[], textSlots: PersonalisationSlot[]): { rows: PairedRow[] } {
  const rows: PairedRow[] = componentSlots.map((componentSlot, index) => ({
    index,
    componentSlot,
    textSlot: textSlots[index] ?? null,
    yearSlot: null
  }));

  return { rows };
}

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * Builds the paired slot model for a step in PAIRED slot mode.
 *
 * COMPONENT slots ordered by `orderIndex` become the rows. Each ALPHANUMERIC slot joins the row
 * whose COMPONENT slot shares its numeric `orderIndex`; within a row a numbers-only slot
 * (`allowNumbers && !allowLetters`) is the **year** and any other is the **moment**. Pairing is
 * by `orderIndex` on purpose — `groupName` cannot be used because processLineItems emits a
 * `_Group Name` line item property for any slot that carries one.
 *
 * The same rules are written for the admin in `apps/gadget/web/utils/pairedRows.ts`; change both
 * together. Rows are numbered from 1: a text slot with no `orderIndex`, or 0, never joins a row.
 *
 * In order:
 * 1. Stones with distinct `orderIndex` values and at least one text slot sharing one: pair by
 *    `orderIndex`. A text slot that matches no stone goes to `unmatchedSlots` with a warning.
 * 2. No text slots: stones only, no warning.
 * 3. Legacy shape (no text slot shares a stone's `orderIndex`, as many text slots as stones, none
 *    numbers-only): zip by position as moments, with a warning to set the rows in the admin.
 * 4. Anything else (years without shared indexes, a count mismatch, stones sharing an index):
 *    stones only, every text slot in `unmatchedSlots`, and `logError`. Never guess.
 *
 * Text is optional: a PAIRED step may be stones only, and a row may have a moment and no year (or
 * neither). Those are normal configurations, not mismatches. A warning is logged only for a real
 * one — two years or two moments in one row, or a text slot that matches no row — and the extra
 * slots go to `unmatchedSlots`.
 *
 * The underlying data stays ordinary slots, so validation, cart, pricing, visualiser and line
 * item properties behave exactly as for a FIXED step.
 *
 * @param options - Options object.
 * @param options.slots - Every slot on the step, in any order.
 * @returns The rows to render plus any slots that could not be paired.
 */
export function buildPairedSlotModel({ slots }: { slots: PersonalisationSlot[] }): PairedSlotModel {
  const componentSlots = sortSlotsByOrderIndex(slots.filter((slot) => slot.slotType === COMPONENT_SLOT_TYPE));
  const textSlots = sortSlotsByOrderIndex(slots.filter((slot) => slot.slotType === TEXT_SLOT_TYPE));
  const otherSlots = slots.filter(
    (slot) => slot.slotType !== COMPONENT_SLOT_TYPE && slot.slotType !== TEXT_SLOT_TYPE
  );

  const rows: PairedRow[] = componentSlots.map((componentSlot, index) => ({
    index,
    componentSlot,
    textSlot: null,
    yearSlot: null
  }));

  const rowsByOrderIndex = new Map<number, PairedRow>();
  for (const row of rows) rowsByOrderIndex.set(getOrderIndex(row.componentSlot), row);

  const hasDistinctStoneOrder = rowsByOrderIndex.size === rows.length;
  const pairsByOrderIndex =
    hasDistinctStoneOrder &&
    textSlots.some((textSlot) => {
      const rowNumber = getRowNumber(textSlot);
      return rowNumber !== null && rowsByOrderIndex.has(rowNumber);
    });

  if (!pairsByOrderIndex) {
    if (textSlots.length === 0) {
      return { rows, unmatchedSlots: otherSlots };
    }

    // Today's sandbox shape, from before rows could be set in the admin: one moment per stone, no
    // years, nothing sharing an index. Paired by position so it keeps working until converted.
    const isLegacyMomentsOnly =
      textSlots.length === componentSlots.length && !textSlots.some((textSlot) => isYearSlot(textSlot));
    if (isLegacyMomentsOnly) {
      reportOnce('pairedByPosition', textSlots, () =>
        logWarning({
          label: 'buildPairedSlotModel',
          warning: 'PAIRED step is paired by position',
          message: 'Its text slots share no orderIndex with a stone. Set its rows in the admin (Rows view).',
          extraData: { componentSlotCount: componentSlots.length, textSlotCount: textSlots.length }
        })
      );
      const { rows: positionalRows } = zipByPosition(componentSlots, textSlots);
      return { rows: positionalRows, unmatchedSlots: otherSlots };
    }

    // Anything else would be a guess (a year could land in a moment's place, or a moment under the
    // wrong stone), so the step shows its stones only and the config is reported instead.
    reportOnce('cannotPair', textSlots, () =>
      logError({
        label: 'buildPairedSlotModel',
        error: new Error('PAIRED step text slots cannot be paired with its stones'),
        message: 'Set the step rows in the admin: each year and moment needs the orderIndex of its stone.',
        extraData: {
          componentSlotCount: componentSlots.length,
          textSlotCount: textSlots.length,
          textSlotIds: textSlots.map((slot) => slot.id)
        }
      })
    );
    return { rows, unmatchedSlots: [...textSlots, ...otherSlots] };
  }

  const unpairedTextSlots: PersonalisationSlot[] = [];
  const duplicateTextSlots: PersonalisationSlot[] = [];

  for (const textSlot of textSlots) {
    const rowNumber = getRowNumber(textSlot);
    const row = rowNumber === null ? undefined : rowsByOrderIndex.get(rowNumber);
    if (!row) {
      unpairedTextSlots.push(textSlot);
      continue;
    }

    const role = isYearSlot(textSlot) ? 'yearSlot' : 'textSlot';
    if (row[role]) {
      duplicateTextSlots.push(textSlot);
      continue;
    }
    row[role] = textSlot;
  }

  if (unpairedTextSlots.length > 0 || duplicateTextSlots.length > 0) {
    logWarning({
      label: 'buildPairedSlotModel',
      warning: 'PAIRED step has text slots that do not pair with a stone',
      message:
        'A text slot joins the stone with the same orderIndex, one year and one moment per stone; the rest are not rendered.',
      extraData: {
        componentSlotCount: componentSlots.length,
        textSlotCount: textSlots.length,
        unpairedTextSlotIds: unpairedTextSlots.map((slot) => slot.id),
        duplicateTextSlotIds: duplicateTextSlots.map((slot) => slot.id)
      }
    });
  }

  return { rows, unmatchedSlots: [...unpairedTextSlots, ...duplicateTextSlots, ...otherSlots] };
}
